import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, Search, Download, Building2, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom tooltip component (defined outside to avoid re-creation)
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-blue-600 font-bold">{payload[0].value.toLocaleString()} units</p>
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const { getAuthHeaders } = useAuth();
  
  // Data state
  const [depots, setDepots] = useState([]);
  const [items, setItems] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [itemDrilldown, setItemDrilldown] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedScope, setSelectedScope] = useState('all'); // 'all' or depot_id
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDropdown, setShowItemDropdown] = useState(false);

  // Fetch depots list
  const fetchDepots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/depots`, { headers: getAuthHeaders() });
      setDepots(res.data);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  }, [getAuthHeaders]);

  // Fetch all item names for autocomplete
  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/item-masters/autocomplete`, { headers: getAuthHeaders() });
      setItems(res.data || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  }, [getAuthHeaders]);

  // Fetch sales trend data (bar chart)
  const fetchSalesTrend = useCallback(async (depotId = null) => {
    try {
      let url = `${API}/analytics/sales-trend`;
      if (depotId && depotId !== 'all') {
        url += `?depot_id=${depotId}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setSalesTrend(res.data);
    } catch (error) {
      console.error('Error fetching sales trend:', error);
    }
  }, [getAuthHeaders]);

  // Fetch drilldown data for selected item
  const fetchItemDrilldown = useCallback(async (itemName) => {
    try {
      const res = await axios.get(`${API}/analytics/item-drilldown?item_name=${encodeURIComponent(itemName)}`, { 
        headers: getAuthHeaders() 
      });
      setItemDrilldown(res.data);
    } catch (error) {
      console.error('Error fetching item drilldown:', error);
      toast.error('Failed to load item details');
    }
  }, [getAuthHeaders]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchDepots(), fetchItems(), fetchSalesTrend()]);
      } catch (err) {
        setError('Unable to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update chart when scope changes
  useEffect(() => {
    // Only call if we have depots loaded (after initial load)
    if (depots.length > 0 && selectedScope) {
      fetchSalesTrend(selectedScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope, depots.length]);

  // Filtered items for autocomplete
  const filteredItems = useMemo(() => {
    if (!itemSearch.trim()) return items.slice(0, 50);
    const query = itemSearch.toLowerCase();
    return items
      .filter(item => item.item_name.toLowerCase().includes(query))
      .slice(0, 50);
  }, [items, itemSearch]);

  // Handle item selection
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setItemSearch(item.item_name);
    setShowItemDropdown(false);
    fetchItemDrilldown(item.item_name);
  };

  // Clear item selection
  const clearItemSelection = () => {
    setSelectedItem(null);
    setItemSearch('');
    setItemDrilldown(null);
  };

  // Get months from drilldown data for table columns
  const drilldownMonths = useMemo(() => {
    if (!itemDrilldown?.depot_monthly_sales) return [];
    const allMonths = new Set();
    Object.values(itemDrilldown.depot_monthly_sales).forEach(depotData => {
      Object.keys(depotData).forEach(month => allMonths.add(month));
    });
    return Array.from(allMonths).sort();
  }, [itemDrilldown]);

  // Export drilldown to Excel
  const exportDrilldownToExcel = () => {
    if (!itemDrilldown || !selectedItem) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Depot', ...drilldownMonths, 'Total', 'EMA Avg'];
    const rows = Object.entries(itemDrilldown.depot_monthly_sales).map(([depotName, monthlyData]) => {
      const monthValues = drilldownMonths.map(m => monthlyData[m] || 0);
      const total = monthValues.reduce((a, b) => a + b, 0);
      const ema = itemDrilldown.depot_ema?.[depotName] || 0;
      return [depotName, ...monthValues, total, ema];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Item Sales');
    
    const filename = `${selectedItem.item_name.substring(0, 30)}_sales_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Exported to Excel');
  };

  if (loading) {
    return <LoadingOverlay message="Loading analytics data..." />;
  }

  if (error) {
    return <ErrorState title="Failed to load analytics" message={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Sales Analytics
        </h1>
        <p className="text-slate-500 mt-1">View sales trends and drill down by item</p>
      </div>

      {/* Scope Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500" />
            Select View
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedScope} onValueChange={setSelectedScope}>
            <SelectTrigger className="w-full md:w-80" data-testid="scope-selector">
              <SelectValue placeholder="Select scope" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Entire Company</SelectItem>
              {depots.map(depot => (
                <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Sales Bar Chart */}
      <Card data-testid="sales-trend-chart">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Monthly Sales Quantity
            {selectedScope !== 'all' && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({depots.find(d => d.id === selectedScope)?.name || 'Selected Depot'})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            {salesTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Bar 
                    dataKey="total_sales" 
                    fill="#3b82f6" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                No sales data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Item Drilldown Section */}
      <Card data-testid="item-drilldown-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-500" />
            Item Drilldown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Item Search Autocomplete */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search for an item..."
                value={itemSearch}
                onChange={(e) => {
                  setItemSearch(e.target.value);
                  setShowItemDropdown(true);
                  if (!e.target.value.trim()) {
                    setSelectedItem(null);
                    setItemDrilldown(null);
                  }
                }}
                onFocus={() => setShowItemDropdown(true)}
                className="pl-10 pr-10"
                data-testid="item-search-input"
              />
              {itemSearch && (
                <button
                  onClick={clearItemSelection}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  ×
                </button>
              )}
            </div>
            
            {/* Autocomplete Dropdown */}
            {showItemDropdown && itemSearch && filteredItems.length > 0 && !selectedItem && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredItems.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleItemSelect(item)}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                  >
                    <p className="font-medium text-slate-900 text-sm">{item.item_name}</p>
                    <p className="text-xs text-slate-500">SKU: {item.sku}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Drilldown Table */}
          {selectedItem && itemDrilldown && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedItem.item_name}</h3>
                  <p className="text-sm text-slate-500">SKU: {selectedItem.sku}</p>
                </div>
                <Button variant="outline" size="sm" onClick={exportDrilldownToExcel}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium">Total Sold</p>
                  <p className="text-xl font-bold text-blue-700">{itemDrilldown.total_quantity?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600 font-medium">EMA Avg (Company)</p>
                  <p className="text-xl font-bold text-green-700">{itemDrilldown.company_ema?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-medium">Depots With Sales</p>
                  <p className="text-xl font-bold text-purple-700">{Object.keys(itemDrilldown.depot_monthly_sales || {}).length}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-amber-600 font-medium">Months Active</p>
                  <p className="text-xl font-bold text-amber-700">{drilldownMonths.length}</p>
                </div>
              </div>

              {/* Sales Table: Rows = Depots, Columns = Months */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-4 py-3 font-semibold text-slate-700 sticky left-0 bg-slate-50 min-w-[150px]">
                          Depot
                        </th>
                        {drilldownMonths.map(month => (
                          <th key={month} className="text-right px-4 py-3 font-semibold text-slate-700 min-w-[80px]">
                            {month}
                          </th>
                        ))}
                        <th className="text-right px-4 py-3 font-semibold text-slate-700 bg-slate-100 min-w-[80px]">
                          Total
                        </th>
                        <th className="text-right px-4 py-3 font-semibold text-slate-700 bg-green-50 min-w-[80px]">
                          EMA Avg
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(itemDrilldown.depot_monthly_sales || {}).map(([depotName, monthlyData], idx) => {
                        const monthValues = drilldownMonths.map(m => monthlyData[m] || 0);
                        const total = monthValues.reduce((a, b) => a + b, 0);
                        const ema = itemDrilldown.depot_ema?.[depotName] || 0;
                        
                        return (
                          <tr key={depotName} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-4 py-2.5 font-medium text-slate-900 sticky left-0 bg-inherit border-r">
                              {depotName}
                            </td>
                            {drilldownMonths.map(month => (
                              <td key={month} className="text-right px-4 py-2.5 text-slate-700 tabular-nums">
                                {(monthlyData[month] || 0).toLocaleString()}
                              </td>
                            ))}
                            <td className="text-right px-4 py-2.5 font-semibold text-slate-900 bg-slate-100/50 tabular-nums">
                              {total.toLocaleString()}
                            </td>
                            <td className="text-right px-4 py-2.5 font-semibold text-green-700 bg-green-50/50 tabular-nums">
                              {ema.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Company Total Row */}
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-4 py-2.5 font-bold text-blue-900 sticky left-0 bg-blue-50 border-r">
                          Company Total
                        </td>
                        {drilldownMonths.map(month => {
                          const monthTotal = Object.values(itemDrilldown.depot_monthly_sales || {})
                            .reduce((sum, depotData) => sum + (depotData[month] || 0), 0);
                          return (
                            <td key={month} className="text-right px-4 py-2.5 font-bold text-blue-900 tabular-nums">
                              {monthTotal.toLocaleString()}
                            </td>
                          );
                        })}
                        <td className="text-right px-4 py-2.5 font-bold text-blue-900 bg-blue-100/50 tabular-nums">
                          {itemDrilldown.total_quantity?.toLocaleString() || 0}
                        </td>
                        <td className="text-right px-4 py-2.5 font-bold text-green-800 bg-green-100/50 tabular-nums">
                          {itemDrilldown.company_ema?.toLocaleString() || 0}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Empty state when no item selected */}
          {!selectedItem && (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Search and select an item above to see detailed sales breakdown</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
